import {
  Component,
  computed,
  DestroyRef,
  effect,
  ElementRef,
  inject,
  Injector,
  input,
  model,
  output,
  signal,
  Signal,
  WritableSignal
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  CreateTaskDialogData,
  CreateTaskModalResultData
} from '@app/components/dialogs/create-task-dialog/create-task-dialog.component';
import { TaskDetailsActionsMenuComponent } from '@app/components/task/task-splitview-list/task-details-content/task-details-actions-menu/task-details-actions-menu.component';
import { TaskDetailsDescriptionTabComponent } from '@app/components/task/task-splitview-list/task-details-content/task-details-description-tab/task-details-description-tab.component';
import { TaskDetailsFilesTabComponent } from '@app/components/task/task-splitview-list/task-details-content/task-details-files-tab/task-details-files-tab.component';
import { TaskDetailsMobileScreenComponent } from '@app/components/task/task-splitview-list/task-details-content/task-details-mobile-screen/task-details-mobile-screen.component';
import { TaskDetailsPrivateNotesTabComponent } from '@app/components/task/task-splitview-list/task-details-content/task-details-private-notes-tab/task-details-private-notes-tab.component';
import { TaskDetailsSubtasksSectionComponent } from '@app/components/task/task-splitview-list/task-details-content/task-details-subtasks-section/task-details-subtasks-section.component';
import { TaskDetailsTab } from '@app/components/task/task-splitview-list/task-details-content/task-details-tab.enum';
import { TaskDetailsTimeReportsTabComponent } from '@app/components/task/task-splitview-list/task-details-content/task-details-time-reports-tab/task-details-time-reports-tab.component';
import { TaskDetailsWideScreenComponent } from '@app/components/task/task-splitview-list/task-details-content/task-details-wide-screen/task-details-wide-screen.component';
import { TasksActionsService } from '@app/shared/services/actions/tasks-actions.service';
import { FileDataService, FileUrlParams } from '@app/shared/services/file-data.service';
import { LocationsDataService } from '@app/shared/services/locations-data.service';
import { ProjectIndustriesDataService } from '@app/shared/services/project-industries-data.service';
import { DeviceService } from '@app/shared/services/shared/device.service';
import { DialogService } from '@app/shared/services/shared/dialog.service';
import { TaskActivitiesDataService } from '@app/shared/services/task-activities-data.service';
import { TaskDescriptionAcknowledgementsDataService } from '@app/shared/services/task-description-acknowledgements-data.service';
import { TaskParticipantsDataService } from '@app/shared/services/task-participants-data.service';
import { TaskTypesDataService } from '@app/shared/services/task-types-data.service';
import { FileContext, UploadStatus } from '@app/shared/types/enums/file-enums';
import { TaskPriority } from '@app/shared/types/enums/task-priority.enum';
import { TaskStatus } from '@app/shared/types/enums/task-status.enum';
import { FileInfo } from '@app/shared/types/models/files/file-info';
import { FileUploadInfo } from '@app/shared/types/models/files/file-upload-info';
import { UploadData } from '@app/shared/types/models/files/upload-data.model';
import { TaskType } from '@app/shared/types/models/task/task-type.model';
import { Task } from '@app/shared/types/models/task/task.model';
import { ActiveTaskService } from '@app/views/company/projects/tasks/active-task.service';
import { TranslateService } from '@ngx-translate/core';

/**
 * TaskDetailsContentComponent - Orchestrates task detail panel layout, tabs, and child sections.
 *
 * Scope: Component-level (used inside task splitview).
 *
 * Usage: Rendered by {@link TaskSplitviewListComponent} when a task is selected.
 */
@Component({
  selector: 'proffeo-task-details-content',
  templateUrl: './task-details-content.component.html',
  styleUrl: './task-details-content.component.scss',
  host: {
    class: 'block min-w-0 max-w-full overflow-x-hidden',
    '[class.h-full]': '!isMobile()',
    '[class.min-h-0]': '!isMobile()',
    '[class.task-details-column--desktop]': '!isMobile()'
  },
  imports: [
    TaskDetailsMobileScreenComponent,
    TaskDetailsWideScreenComponent,
    TaskDetailsDescriptionTabComponent,
    TaskDetailsFilesTabComponent,
    TaskDetailsTimeReportsTabComponent,
    TaskDetailsPrivateNotesTabComponent,
    TaskDetailsSubtasksSectionComponent,
    TaskDetailsActionsMenuComponent
  ]
})
export class TaskDetailsContentComponent {
  private readonly subtasksPanelSyncedTaskId: WritableSignal<string | undefined> = signal(undefined);
  private readonly hostElement = inject(ElementRef<HTMLElement>);

  private readonly activeTaskService = inject(ActiveTaskService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly injector = inject(Injector);
  private readonly taskActionsService = inject(TasksActionsService);
  private readonly fileService = inject(FileDataService);
  private readonly deviceService = inject(DeviceService);
  private readonly dialogService = inject(DialogService);
  private readonly projectIndustriesService = inject(ProjectIndustriesDataService);
  private readonly taskTypesDataService = inject(TaskTypesDataService);
  private readonly taskActivitiesDataService = inject(TaskActivitiesDataService);
  private readonly taskParticipantsDataService = inject(TaskParticipantsDataService);
  private readonly locationsDataService = inject(LocationsDataService);
  private readonly descriptionAcknowledgementsData = inject(TaskDescriptionAcknowledgementsDataService);
  private readonly translateService = inject(TranslateService);

  protected readonly isMobile: Signal<boolean> = this.deviceService.isMobile;
  protected readonly MAX_TASK_NAME_LENGTH = 100;
  protected readonly TaskDetailsTab = TaskDetailsTab;
  protected readonly FileContext = FileContext;

  protected readonly files = computed(() =>
    this.fileService.getFiles({
      context: FileContext.taskFile,
      taskId: this.task().id
    })()
  );
  protected readonly descriptionFiles = computed(() =>
    this.fileService.getFiles({
      context: FileContext.taskDescriptionFile,
      taskId: this.task().id
    })()
  );
  protected readonly activeUploads: Signal<FileUploadInfo[]> = computed(() =>
    this.fileService
      .getActiveUploads(this.task()?.id)()
      .filter(
        upload =>
          (upload.context === FileContext.taskFile || upload.context === FileContext.taskDescriptionFile) &&
          (upload.status === UploadStatus.Started ||
            upload.status === UploadStatus.Failure ||
            upload.status === UploadStatus.Canceled)
      )
  );
  protected readonly taskParticipants = computed(
    () => this.taskParticipantsDataService.getTaskParticipants(this.task()?.id)() ?? []
  );

  protected readonly showTaskSubtasksPanel: WritableSignal<boolean> = signal(true);
  protected readonly isTaskSubtasksPanelContentVisible: Signal<boolean> = computed(() => {
    const subtasksCount = this.task().subtasksCount ?? 0;
    return subtasksCount !== 0 && this.showTaskSubtasksPanel();
  });
  protected readonly descriptionHasUnsavedChanges: WritableSignal<boolean> = signal(false);

  protected readonly activeCompanyTaskTypes: Signal<TaskType[]> = this.taskTypesDataService.getActiveCompanyTaskTypes();
  protected readonly isNameEditing: WritableSignal<boolean> = signal(false);

  protected readonly projectIndustries = computed(() =>
    this.projectIndustriesService.getProjectIndustries(this.task().project()?.id)()
  );
  protected readonly projectLocations = computed(() =>
    this.locationsDataService.getProjectLocations(this.task().project()?.id)()
  );

  public readonly currentTabIndex = model<TaskDetailsTab>(TaskDetailsTab.Description);
  public readonly task: Signal<Task> = this.activeTaskService.activeTask;
  public readonly activeTaskSubtasks: Signal<Task[]> = this.activeTaskService.activeTaskSubtasks;
  public readonly showReopenActivity = input(false);
  public readonly activityReopenRequested = output<void>();

  public constructor() {
    effect(() => {
      const currentTask = this.task();
      const taskId = currentTask?.id;
      if (!taskId) {
        return;
      }
      if (taskId !== this.subtasksPanelSyncedTaskId()) {
        this.subtasksPanelSyncedTaskId.set(taskId);
        this.showTaskSubtasksPanel.set(true);
      }
    });
  }

  public scrollToTop(): void {
    const panelBody = this.hostElement.nativeElement.querySelector(
      '.proffeo-ui-detail-panel-body'
    ) as HTMLElement | null;
    if (panelBody) {
      panelBody.scrollTop = 0;
    }
  }

  protected onScreenTabSelected(payload: { index: TaskDetailsTab; event: MouseEvent }): void {
    payload.event.preventDefault();
    this.onTabSelected(payload.index);
  }

  protected onDescriptionUnsavedChangesChange(hasUnsavedChanges: boolean): void {
    this.descriptionHasUnsavedChanges.set(hasUnsavedChanges);
    this.activeTaskService.setDescriptionHasUnsavedChanges(hasUnsavedChanges);
  }

  protected onPanelClosed(): void {
    void this.activeTaskService.clearActiveTask();
  }

  protected onReopenActivity(): void {
    this.activityReopenRequested.emit();
  }

  protected onDesktopSubtasksPanelExpandedChange(isExpanded: boolean): void {
    this.showTaskSubtasksPanel.set(isExpanded);
  }

  protected onAddSubtaskFromPanelClick(event: MouseEvent): void {
    event.stopPropagation();
    this.startSubtaskCreation();
  }

  protected startSubtaskCreation(): void {
    const data: CreateTaskDialogData = {
      project: this.task().project,
      taskTypes: this.taskTypesDataService.getActiveCompanyTaskTypes(),
      parentTask: this.task(),
      projectIndustries: this.projectIndustries,
      projectLocations: this.projectLocations,
      taskPrototype: this.task()
    };
    this.openCreateSubtaskDialog(data);
  }

  private openCreateSubtaskDialog(data: CreateTaskDialogData): void {
    this.dialogService
      .openCreateTaskDialog(data, this.injector)
      .afterClosed()
      .subscribe((result: CreateTaskModalResultData) => {
        if (result.reopenModal) {
          this.openCreateSubtaskDialog(data);
        }
      });
  }

  protected onSubtaskClick(subtask: Task): void {
    void this.activeTaskService.setActiveTask(subtask);
  }

  protected openTimeReportDialog(task: Task): void {
    this.dialogService
      .openTimeReportDialog(task)
      .afterClosed()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.fileService.reloadTaskFiles(task.id);
        }
      });
  }

  protected onTimeReportsTabRequested(): void {
    this.onTabSelected(TaskDetailsTab.TimeReports);
  }

  protected onFilesAdded(files: File[], context: FileContext.taskDescriptionFile | FileContext.taskFile): void {
    const uploadParams: FileUrlParams = {
      context,
      taskId: this.task().id
    };
    files.forEach(file => {
      const uploadData: UploadData = {
        file,
        description: '',
        fileTags: [],
        orderNo: this.files()?.length ?? 0
      };
      this.fileService.uploadFile(uploadParams, uploadData);
    });
  }

  protected onFileDelete(file: FileInfo, context: FileContext.taskFile | FileContext.taskDescriptionFile): void {
    const taskId = this.task().id;
    this.dialogService
      .openConfirmationDialog({
        title: this.translateService.instant('delete-file-modal-delete'),
        desc: this.translateService.instant('delete-file-modal-confirmation', {
          fileName: file.name
        })
      })
      .afterClosed()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(confirmed => {
        if (confirmed) {
          this.fileService.deleteFile({ context, taskId }, file);
        }
      });
  }

  protected onSetFileAsAvatar(file: FileInfo): void {
    this.fileService.setTaskAvatar(this.task().id, file.id);
  }

  protected onTaskNameChanged(name: string): void {
    this.taskActionsService.updateTaskName(this.task(), name).subscribe(() => this.isNameEditing.set(false));
  }

  protected onNameEditRequested(): void {
    this.isNameEditing.set(true);
  }

  protected onNameEditCancelled(): void {
    this.isNameEditing.set(false);
  }

  protected onTaskTypeSelected(type: TaskType): void {
    this.taskActionsService.updateTaskType(this.task(), type);
  }

  protected changeDescription(description: string): void {
    const task = this.task();

    if (!task?.acknowledgementRequested) {
      this.saveDescription(task, description);
      return;
    }

    this.dialogService
      .openRequireDescriptionAcknowledgementAgainDialog()
      .afterClosed()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(requireConfirmationAgain => {
        if (requireConfirmationAgain === undefined) {
          return;
        }

        this.saveDescription(task, description, requireConfirmationAgain);
      });
  }

  protected onStatusSelected(status: TaskStatus, task: Task): void {
    this.taskActionsService.updateTaskStatus(task, status);
  }

  protected onPrioritySelected(priority: TaskPriority, task: Task): void {
    this.taskActionsService.updateTaskPriority(task, priority);
  }

  protected onProgressSelected(percentage: number, task: Task): void {
    this.taskActionsService.updateTaskProgress(task, percentage);
  }

  private saveDescription(task: Task, description: string, requireConfirmationAgain?: boolean): void {
    this.taskActionsService.updateTaskDescription(task, description, requireConfirmationAgain).subscribe(() => {
      if (task.acknowledgementRequested) {
        this.descriptionAcknowledgementsData.reloadAcknowledgements(task.id);
      }
    });
  }

  protected onTabSelected(index: TaskDetailsTab): void {
    const previousIndex = this.currentTabIndex();
    this.currentTabIndex.set(index);
    if (index === TaskDetailsTab.Description && previousIndex !== TaskDetailsTab.Description) {
      const taskId = this.task()?.id;
      if (taskId) {
        this.taskActivitiesDataService.reloadTaskActivities(taskId);
      }
    }
  }
}
